import { useCallback, type ChangeEvent, useState } from 'react';
import { TextInput } from '@cbhq/cds-web/controls';
import { Switch } from '@cbhq/cds-web/controls';
import { TextLabel1 } from '@cbhq/cds-web/typography';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { useGlobalSettings } from './GlobalSettingsProvider';
import { DEFAULT_TX_DEADLINE_SETTINGS } from './defaultSettings';

const DEADLINE_SETTINGS_INPUT_TEST_ID = 'deadline-settings-input-field';
const DEADLINE_SETTINGS_SWITCH_TEST_ID = 'deadline-settings-switch-field';

// Hoist static RegExp pattern to avoid recreating on every render
const DEADLINE_DIGITS_REGEX = /^\d+/;

export type TxDeadlineSettingValue = {
  txExpireIn: number; // in seconds
  autoTxDeadline: boolean;
};

export type TxDeadlineSettingProps = {
  label: string;
  settingKey: string;
};

// Convert seconds to minutes for display
const secondsToMinutes = (seconds: number) => Math.round(seconds / 60);
const minutesToSeconds = (minutes: number) => minutes * 60;

export function TxDeadlineSetting({ label, settingKey }: TxDeadlineSettingProps) {
  const { getSettingValue, updateSetting } = useGlobalSettings();

  const value = getSettingValue(settingKey) as TxDeadlineSettingValue;
  const [expireInMinutes, setExpireInMinutes] = useState(
    // more robust way to handle the value
    value?.txExpireIn ? secondsToMinutes(value.txExpireIn) : '',
  );

  // Get current values from props with defaults
  const isAutoDeadline = value?.autoTxDeadline ?? false;

  const handleEditDeadline = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setExpireInMinutes(inputValue);

      if (!isAutoDeadline && !isNaN(Number(inputValue)) && Number(inputValue) >= 0) {
        // Only allow whole numbers for minutes
        const match = inputValue.match(DEADLINE_DIGITS_REGEX);
        if (match) {
          const minutes = parseInt(match[0], 10);
          updateSetting(settingKey, {
            ...value,
            txExpireIn: minutesToSeconds(minutes),
          });
        }
      }
    },
    [isAutoDeadline, updateSetting, value.txExpireIn, settingKey],
  );

  const handleToggleAutoDeadline = useCallback(() => {
    const newAutoDeadline = !isAutoDeadline;
    const newExpireInSeconds = newAutoDeadline
      ? DEFAULT_TX_DEADLINE_SETTINGS.txExpireIn
      : value.txExpireIn;
    const newExpireInMinutes = secondsToMinutes(newExpireInSeconds);
    setExpireInMinutes(newExpireInMinutes);
    updateSetting(settingKey, {
      txExpireIn: newExpireInSeconds,
      autoTxDeadline: newAutoDeadline,
    });
  }, [isAutoDeadline, updateSetting, value.txExpireIn, settingKey]);

  return (
    <VStack gap={2}>
      <HStack gap={1} alignItems="center">
        <TextLabel1 as="p">{label}</TextLabel1>
        <Tooltip
          content="Your transaction will revert if it is pending for longer than this duration."
          placement="top"
        >
          <Icon name="info" size="xs" color="fgMuted" active />
        </Tooltip>
      </HStack>
      <HStack alignItems="center">
        <TextInput
          testID={DEADLINE_SETTINGS_INPUT_TEST_ID}
          compact
          disabled={isAutoDeadline}
          type="number"
          value={expireInMinutes.toString()}
          onChange={handleEditDeadline}
          placeholder="30"
          suffix="min"
        />
        <HStack style={{ scale: 0.8, transformOrigin: 'center right' }}>
          <Switch
            testID={DEADLINE_SETTINGS_SWITCH_TEST_ID}
            onChange={handleToggleAutoDeadline}
            checked={isAutoDeadline}
          >
            Default
          </Switch>
        </HStack>
      </HStack>
    </VStack>
  );
}
